import { ref } from 'vue';
import { hasAuthSession } from '@/utils/auth-token';
import { ensurePrivyAuthReady, initPrivy, isPrivyConfigured } from '@/utils/privy';
import {
  ensureWalletConnectSdkReady,
  isWalletConnectConfigured,
} from '@/utils/wallet-connect-session';
import { isStorageBlockedError } from '@/utils/client-storage';

/** 用户点击登录动作前，等待 SDK 就绪的最长时间 */
export const AUTH_BOOTSTRAP_WAIT_MS = 15_000;
/** 扫码页无 QR：5s 展示进度提示 */
export const WC_QR_PROGRESS_MS = 5_000;
/** 扫码页无 QR：10s 终止 */
export const WC_QR_TERMINATE_MS = 10_000;

/** @typedef {'idle'|'warming'|'ready'|'failed'|'paused'} BootstrapLaneStatus */

/** @type {import('vue').Ref<BootstrapLaneStatus>} */
const privyStatus = ref('idle');
/** @type {import('vue').Ref<BootstrapLaneStatus>} */
const wcStatus = ref('idle');

/** @type {Promise<void>|null} */
let privyWarmPromise = null;

function withTimeout(promise, ms, message) {
  let timer;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

function isPaused(status) {
  return status === 'failed' || status === 'paused';
}

function markPrivyPaused(err) {
  console.warn('[auth-bootstrap] privy paused', err);
  privyStatus.value = 'paused';
  privyWarmPromise = null;
}

function markWcPaused(err) {
  console.warn('[auth-bootstrap] walletconnect paused', err);
  wcStatus.value = 'paused';
}

/** 登出后重置，下次未登录可重新预热 */
export function resetAuthBootstrapOnLogout() {
  privyStatus.value = 'idle';
  wcStatus.value = 'idle';
  privyWarmPromise = null;
}

async function warmPrivyLaneOnce() {
  if (!isPrivyConfigured()) {
    privyStatus.value = 'ready';
    return;
  }
  privyStatus.value = 'warming';
  try {
    await initPrivy();
    privyStatus.value = 'ready';
  } catch (err) {
    markPrivyPaused(err);
    // 存储被拦：授权后可 force 再试，勿永久 pause
    if (isStorageBlockedError(err)) {
      privyStatus.value = 'idle';
      privyWarmPromise = null;
    }
    throw err;
  }
}

/**
 * 后台单次 Privy 预热（不自动重试）
 * @param {{ force?: boolean }} [options]
 */
export function startPrivyWarmup({ force = false } = {}) {
  if (hasAuthSession()) return Promise.resolve();
  if (!force && isPaused(privyStatus.value)) {
    return Promise.resolve();
  }
  if (privyStatus.value === 'ready') return Promise.resolve();
  if (privyWarmPromise) return privyWarmPromise;

  if (force) {
    privyStatus.value = 'idle';
    privyWarmPromise = null;
  }

  privyWarmPromise = warmPrivyLaneOnce().finally(() => {
    privyWarmPromise = null;
  });
  return privyWarmPromise;
}

/** 未登录时 App / 弹窗打开：单次 Privy 预热；已 pause 则不再后台重试 */
export function startAuthBootstrap() {
  if (hasAuthSession()) return Promise.resolve();
  return startPrivyWarmup().catch(() => {});
}

/**
 * 邮箱 / Google：用户主动触发，单次尝试（失败则 pause）
 */
export async function ensurePrivyLoginReady({
  force = true,
  timeoutMs = AUTH_BOOTSTRAP_WAIT_MS,
} = {}) {
  if (hasAuthSession()) return;
  if (force) {
    privyStatus.value = 'idle';
    privyWarmPromise = null;
  } else if (isPaused(privyStatus.value)) {
    throw new Error('privy auth prepare timeout');
  }
  try {
    await withTimeout(ensurePrivyAuthReady({ timeoutMs }), timeoutMs, 'privy auth prepare timeout');
    privyStatus.value = 'ready';
  } catch (err) {
    markPrivyPaused(err);
    if (isStorageBlockedError(err)) {
      privyStatus.value = 'idle';
      privyWarmPromise = null;
    }
    throw err;
  }
}

/**
 * WalletConnect 扫码：用户点击时单次 init（失败 pause，下次扫码再试）
 */
export async function ensureWalletConnectLoginReady({ timeoutMs = AUTH_BOOTSTRAP_WAIT_MS } = {}) {
  if (!isWalletConnectConfigured() || hasAuthSession()) return;

  wcStatus.value = 'warming';
  try {
    await withTimeout(
      ensureWalletConnectSdkReady({ timeoutMs }),
      timeoutMs,
      'walletconnect sdk prepare timeout'
    );
    wcStatus.value = 'ready';
  } catch (err) {
    markWcPaused(err);
    throw err;
  }
}

export function useAuthBootstrap() {
  return {
    privyStatus,
    wcStatus,
    startAuthBootstrap,
    resetAuthBootstrapOnLogout,
    startPrivyWarmup,
    ensurePrivyLoginReady,
    ensureWalletConnectLoginReady,
  };
}
